/**
 * Cloudflare Zaraz client shim (docs/features/active/zaraz-migration/).
 *
 * `window.zaraz` exists only on hostnames served through the Cloudflare zone
 * (dupe.com / dupe.dev) — localhost and *.vercel.app previews bypass the zone,
 * and the script is injected at the edge (auto-inject), never bundled. Every
 * call site must therefore go through this shim so the existence guard and the
 * rollout flag live in one place.
 *
 * NEXT_PUBLIC_ZARAZ_TRACK_ENABLED gates the dual-fire rollout (Phase 2):
 * while it is unset/false, Zaraz still serves its own edge pageviews on zones
 * where it is enabled, but app-emitted events keep flowing only to the legacy
 * sinks. Absence of the var means OFF.
 */

interface ZarazApi {
  track: (event: string, properties?: Record<string, unknown>) => void
  set: (key: string, value: unknown, options?: Record<string, unknown>) => void
}

const getZaraz = (): ZarazApi | undefined =>
  typeof window === 'undefined'
    ? undefined
    : (window as Window & { zaraz?: ZarazApi }).zaraz

export const isZarazTrackEnabled = (): boolean =>
  process.env.NEXT_PUBLIC_ZARAZ_TRACK_ENABLED === 'true'

/**
 * Fire an event to Zaraz (edge fan-out to GA4/Meta managed components).
 * No-op when the rollout flag is off or Zaraz isn't on this hostname; never
 * throws — analytics must not break the app.
 *
 * Returns whether the event actually reached `zaraz.track()`. Every no-op here
 * is silent and often temporary (the flag is off, or the edge script has not
 * attached yet), so a caller that persists a one-shot "already sent" mark needs
 * to tell a real dispatch from a drop — see `googleAdsConversions`.
 */
export const zarazTrack = (
  event: string,
  properties?: Record<string, unknown>
): boolean => {
  if (!isZarazTrackEnabled()) return false

  const zaraz = getZaraz()
  if (!zaraz) return false

  try {
    zaraz.track(event, properties)
    return true
  } catch {
    // Swallow: zaraz internals (config republish windows, blocked /t POSTs)
    // must never surface as app errors. Zaraz-sourced $exception noise is
    // separately filtered in posthogBrowserConfig.
    return false
  }
}

/**
 * Shared event id for Meta Pixel ↔ Conversions API deduplication: the same id
 * is passed as the pixel's `eventID` and as `event_id` in the Zaraz payload so
 * Meta can collapse the browser and server copies of one event during the
 * dual-fire phase.
 * https://developers.facebook.com/docs/marketing-api/conversions-api/deduplicate-pixel-and-server-events/
 */
export const generateAnalyticsEventId = (): string => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`
}
